import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { HID_EMAIL_MESSAGE, isHidGlobalEmail } from "../auth/hidEmail";
import {
  AlreadySignedIn,
  createRegistrationFlow,
  RegistrationFlow,
  registerWithPassword,
  submitRegistrationCode,
} from "../auth/kratosRegistration";
import { enrollSecurityKey } from "../auth/kratosSettings";

type Step = "credentials" | "verify" | "verified" | "fido-optional" | "success";

const FLOW_UNAVAILABLE =
  "Cannot reach the identity service. Confirm the Ory stack is running on port 4433.";

interface RegisterFormProps {
  onBackToLogin: () => void;
}

export function RegisterForm({ onBackToLogin }: RegisterFormProps) {
  const { refresh } = useAuth();

  const [flow, setFlow] = useState<RegistrationFlow | null>(null);
  const [step, setStep] = useState<Step>("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [code, setCode] = useState("");
  const [verificationFlowId, setVerificationFlowId] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [fidoAvailable, setFidoAvailable] = useState(false);

  const startFlow = useCallback(async () => {
    try {
      setFlow(await createRegistrationFlow());
    } catch (err) {
      if (err instanceof AlreadySignedIn) {
        await refresh();
        return;
      }
      setError(FLOW_UNAVAILABLE);
    }
  }, [refresh]);

  useEffect(() => {
    void startFlow();
  }, [startFlow]);

  useEffect(() => {
    setFidoAvailable(!!navigator.credentials?.create);
  }, []);

  const handlePasswordRegistration = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password || !confirmPassword) {
      setError("Please fill in all fields.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (!isHidGlobalEmail(trimmedEmail)) {
      setError(HID_EMAIL_MESSAGE);
      return;
    }
    if (!flow) {
      setError(FLOW_UNAVAILABLE);
      return;
    }

    setBusy(true);
    try {
      const result = await registerWithPassword(flow, trimmedEmail, password);
      switch (result.kind) {
        case "verify":
          setVerificationFlowId(result.verificationFlowId);
          setCode("");
          setInfo(`A sign-in code was sent to ${trimmedEmail}. Enter it to finish registration.`);
          setStep("verify");
          break;
        case "success":
          // Verification already issued the session; defer refresh() so the
          // optional security-key step stays on screen.
          setStep(fidoAvailable ? "fido-optional" : "success");
          if (!fidoAvailable) await refresh();
          break;
        case "alreadySignedIn":
          await refresh();
          break;
        case "expired":
          setError("That registration attempt timed out. Please try again.");
          await startFlow();
          break;
        case "error":
          if (result.flow) setFlow(result.flow);
          setError(result.message);
          break;
      }
    } catch {
      setError(FLOW_UNAVAILABLE);
    } finally {
      setBusy(false);
    }
  };

  const handleVerifyCode = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = code.replace(/\s/g, "");
    if (!trimmed) {
      setError("Enter the code from your email.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await submitRegistrationCode(verificationFlowId, trimmed);
      if (result.kind === "verified" || result.kind === "success") {
        // Kratos 1.3.1 does not issue a session after verification; sign in next.
        setStep("verified");
      } else if (result.kind === "error") {
        setError(result.message);
      }
    } catch {
      setError("The code could not be verified. Request a new one from the sign-in page.");
    } finally {
      setBusy(false);
    }
  };

  const finish = async () => {
    setStep("success");
    await refresh();
  };

  const handleFIDO2Enrollment = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await enrollSecurityKey(email.trim() || "Security key");
      if (result.kind === "enrolled") {
        setInfo("Security key added. You can now sign in without a password.");
        await finish();
      } else if (result.kind === "cancelled") {
        setInfo("Security key setup was cancelled. You can add one later from your account.");
      } else {
        setError(result.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const fieldClass =
    "w-full rounded-xl border border-slate-300 px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20";

  if (step === "success") {
    return (
      <div className="mt-6 rounded-xl border border-green-200 bg-green-50 px-4 py-6 text-center">
        <div className="text-lg font-semibold text-green-900">Registration successful</div>
        <p className="mt-2 text-sm text-green-800">
          Your account has been created. Taking you in…
        </p>
      </div>
    );
  }

  if (step === "verified") {
    return (
      <div className="mt-6 rounded-xl border border-green-200 bg-green-50 px-4 py-6 text-center">
        <div className="text-lg font-semibold text-green-900">Email confirmed</div>
        <p className="mt-2 text-sm text-green-800">
          Your @hidglobal.com address is verified. Sign in to continue.
        </p>
        <button
          type="button"
          onClick={onBackToLogin}
          className="mt-4 rounded-xl bg-brand-navy px-4 py-3 text-sm font-semibold text-white transition hover:opacity-90"
        >
          Sign in
        </button>
      </div>
    );
  }

  if (step === "verify") {
    return (
      <div className="mt-6">
        {error && (
          <div role="alert" className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            {error}
          </div>
        )}
        {info && !error && (
          <div className="mb-4 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800">
            {info}
          </div>
        )}
        <form onSubmit={(event) => void handleVerifyCode(event)} className="flex flex-col gap-4">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Email code
            </span>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              autoFocus
              onChange={(e) => setCode(e.target.value)}
              placeholder="6-digit code"
              className={fieldClass}
              disabled={busy}
            />
          </label>
          <button
            type="submit"
            disabled={busy}
            className="rounded-xl bg-brand-navy px-4 py-3 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
          >
            {busy ? "Checking code…" : "Confirm email"}
          </button>
        </form>
      </div>
    );
  }

  if (step === "fido-optional") {
    return (
      <div className="mt-6 flex flex-col gap-4">
        {error && (
          <div role="alert" className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            {error}
          </div>
        )}
        {info && !error && (
          <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800">
            {info}
          </div>
        )}
        <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-6">
          <h3 className="text-lg font-semibold text-blue-900">Secure your account (optional)</h3>
          <p className="mt-2 text-sm text-blue-800">
            Register a FIDO2 security key, fingerprint, or face for passwordless sign-in.
          </p>

          <div className="mt-4 flex flex-col gap-3">
            <button
              type="button"
              onClick={() => void handleFIDO2Enrollment()}
              disabled={busy}
              className="rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
            >
              {busy ? "Waiting for your security key…" : "Register biometric / security key"}
            </button>

            <button
              type="button"
              onClick={() => void finish()}
              disabled={busy}
              className="rounded-xl border border-blue-300 bg-blue-100 px-4 py-3 text-sm font-semibold text-blue-900 transition hover:bg-blue-50 disabled:opacity-50"
            >
              Skip for now
            </button>
          </div>

          <p className="mt-3 text-xs text-blue-700">
            You can add a security key later from your account.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6">
      {error && (
        <div role="alert" className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
          {error}
        </div>
      )}

      <form onSubmit={handlePasswordRegistration} className="flex flex-col gap-4">
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Email address
          </span>
          <input
            type="email"
            value={email}
            autoComplete="email"
            autoFocus
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@hidglobal.com"
            className={fieldClass}
            disabled={busy}
          />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Password
          </span>
          <input
            type="password"
            value={password}
            autoComplete="new-password"
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 8 characters"
            className={fieldClass}
            disabled={busy}
          />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Confirm password
          </span>
          <input
            type="password"
            value={confirmPassword}
            autoComplete="new-password"
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm your password"
            className={fieldClass}
            disabled={busy}
          />
        </label>

        <button
          type="submit"
          disabled={busy || !flow}
          className="rounded-xl bg-brand-navy px-4 py-3 text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
        >
          {busy ? "Creating account…" : "Create account"}
        </button>

        <p className="text-xs text-slate-500">
          Use your @hidglobal.com address. We email a code, and the account is
          usable only after that code matches.
        </p>
      </form>

      <div className="mt-6 border-t border-slate-200 pt-5">
        <p className="text-sm text-slate-600">
          Already have an account?{" "}
          <button
            type="button"
            onClick={onBackToLogin}
            className="font-semibold text-brand-navy hover:underline"
          >
            Sign in
          </button>
        </p>
      </div>
    </div>
  );
}
